import React, { useContext, useMemo } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import LocationSelector from '../LocationSelector';
import { isSSR } from '@front10/utils';
import Box from '../../elements/Box';
import nanoid from 'nanoid';
import { Icon } from '@front10/base-elements';
import AirportIataCode from '../../containers/_components/AirportIataCode';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import { FilterButton } from '../../lab/FilterButton';
import {
  setSelectedOrigin,
  useStore,
  setChangedOrigin
} from './../../containers/InteractiveMapContainer/hooks/useIMCState';

const OriginDestinationFilter = (props, ref) => {
  const {
    origin,
    destination,
    showOrigin,
    showDestination,
    showSwitch,
    showAirportIataCode,
    components,
    showLabels,
    placeholderOriginKey = 'cmp-location-origin-placeholder',
    placeholderDestinationKey = 'cmp-location-destination-placeholder',
    isDirty,
    setWasClear,
    setIsClearOriginFocus,
    setIsClearDestinationFocus,
    isClearOriginFocus,
    isClearDestinationFocus,
    setEnableAriaHiddenFooter,
    setCloseButtonFocus,
    closeButtonFocus,
    forcedLoading = false,
    containerSettings,
    doneButtonLabelKey = 'cmp-origin-destination-done-button',
    clearButtonLabelKey = 'cmp-origin-destination-clear-button',
    titleOriginLabelKey = 'cmp-origin-title',
    titleDestinationLabelKey = 'cmp-destination-title',
    fromLabelKey = 'cmp-origin-destination-filter-button-from-label',
    toLabelKey = 'cmp-origin-destination-filter-button-to-label',
    showLabelfilterButton,
    originDestinationContainerSettings = {},
    showDescriptionLabel,
    showSeparatorDescriptionLabel = false
  } = props;
  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);
  const {
    origin: originSettings,
    destination: destinationSettings,
    columnOriginSettings = {
      'cmp-styles': {
        'max-width': '100%'
      }
    },
    columnDestinationSettings = {
      'cmp-styles': {
        'max-width': '100%'
      }
    }
    // column: columnSettings
  } = components;
  const [id] = React.useState(nanoid());
  const setSelectedOriginFn = useStore(setSelectedOrigin);
  const setChangedOriginFn = useStore(setChangedOrigin);
  const titleOrigin = translate(titleOriginLabelKey, 'Select origin');
  const titleDestination = translate(titleDestinationLabelKey, 'Select destination');
  const originLocationSelectorRef = React.useRef(null);
  const destinationLocationSelectorRef = React.useRef(null);
  const [activeDone, setActiveDone] = React.useState(false);

  function renderItem(item, locationProps, type) {
    return (
      <>
        <AirportIataCode
          item={item}
          showAirportIataCode={showAirportIataCode}
          {...locationProps.listItem}
        />
        {(originSettings.isMobile || destinationSettings.isMobile) && (
          <Icon
            className="fr-rotate"
            icon="check"
            size={20}
            sx={{
              stroke: 'primary',
              verticalAlign: 'middle',
              strokeWidth: '1px',
              visibility:
                type === 'origin'
                  ? origin.valueSelect === item.value
                    ? 'visible'
                    : 'hidden'
                  : destination.valueSelect === item.value
                  ? 'visible'
                  : 'hidden'
            }}
            {...locationProps.listItem.iconCheckProps}
          ></Icon>
        )}
      </>
    );
  }
  const any = translate('cmp-origin-destination-anywhere', 'Anywhere');
  const labelSeparator = translate('cmp-origin-destination-title-separador', ':');

  const originLabel = useMemo(() => {
    if (!origin.value) {
      return translate('cmp-origin-destination-anywhere', 'Anywhere');
    }
    const label = origin.data.filter((i) => i.value === origin.value).pop()?.label;
    if (label) {
      return label;
    }
    return '';
  }, [origin.data, origin.value, translate]);

  const destinationLabel = useMemo(() => {
    if (!destination.value) {
      return translate('cmp-origin-destination-anywhere', 'Anywhere');
    }
    let search = '';
    search = destination.data.filter((i) => i.value === destination.value).pop()?.label;
    return search;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination.data, destination.value, translate]);

  const buttonOriginLabel =
    origin.value && originLabel
      ? `${showDescriptionLabel ? titleOrigin : ''}${
          showSeparatorDescriptionLabel ? labelSeparator : ''
        } ${showLabelfilterButton ? translate(fromLabelKey, 'From') : ''} ${originLabel}`
      : showDescriptionLabel
      ? showSeparatorDescriptionLabel
        ? `${titleOrigin}${labelSeparator} ${any}`
        : `${titleOrigin} ${any}`
      : titleOrigin;

  const buttonDestinationLabel =
    destination.value && destinationLabel
      ? `${showDescriptionLabel ? titleDestination : ''}${
          showSeparatorDescriptionLabel ? labelSeparator : ''
        } ${showLabelfilterButton ? translate(toLabelKey, 'To') : ''} ${destinationLabel}`
      : showDescriptionLabel
      ? showSeparatorDescriptionLabel
        ? `${titleDestination}${labelSeparator} ${any}`
        : `${titleDestination} ${any}`
      : titleDestination;

  //   React.useEffect(() => {
  //     if (
  //       destination.isLoading === false &&
  //       destination.valueSelect !== undefined &&
  //       destinationLabel === undefined
  //     ) {
  //       // destination.onClear();
  //       destination.selectDestination({ value: '' });
  //     }
  //   }, [destination, destinationLabel]);

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (showOrigin) {
          if (!isSSR()) {
            setTimeout(() => {
              document.querySelector(`#toggleElementIdOrigin-${id}`).focus();
            }, 200);
          }
        } else {
          if (!isSSR()) {
            setTimeout(() => {
              document.querySelector(`#toggleElementIdDestination-${id}`).focus();
            }, 200);
          }
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    if (
      origin.value &&
      destination.value &&
      !destination?.isLoading &&
      destination.data.find((i) => i.value === destination.value) === undefined
    ) {
      destination.onClear();
      destination.selectDestination({ value: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin.value, destination.isLoading, destination.value, destination.data]);

  const renderOriginLocationSelector = (
    <FlexibleFilterContainer
      key="originFilter"
      {...containerSettings}
      showSeparator
      saveOnClose={false}
      variant="layout.popover"
      label={titleOrigin}
      title={titleOrigin}
      disabledClearButton={!Boolean(origin.valueSelect)}
      disabledDoneButton={activeDone ? !activeDone : !Boolean(origin.valueSelect)}
      enabledClearButton
      customAccessibilityToggleElement={true}
      labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
      labelClearBtn={translate(clearButtonLabelKey, 'Clear')}
      onClearBtn={() => {
        origin.onClear();
        setActiveDone(true);
        originLocationSelectorRef?.current?.focus();
      }}
      onSave={() => {
        origin.onChange({ value: origin.valueSelect ? origin.valueSelect : undefined });
      }}
      onOpen={() => {
        if (origin.valueSelect !== origin.value) {
          origin.selectOrigin({ value: origin.value });
        }
        if (activeDone) {
          setActiveDone(false);
        }
      }}
      onOpenPopup={() => {
        if (origin.valueSelect !== origin.value) {
          origin.selectOrigin({ value: origin.value });
        }

        if (activeDone) {
          setActiveDone(false);
        }
        setTimeout(() => originLocationSelectorRef?.current.focus(), 100);
      }}
      toggleElement={
        <FilterButton
          {...originSettings.inputItem}
          id={`toggleElementIdOrigin-${id}`}
          value={buttonOriginLabel}
          active={Boolean(origin.value)}
        />
      }
    >
      <Box {...originSettings.originContainerSetting}>
        <LocationSelector
          inputFocusReferences={originLocationSelectorRef}
          flex="1"
          paddingRight={showDestination && !showSwitch ? '2' : undefined}
          showLabel={showLabels}
          {...originSettings}
          defaultValue={origin.valueSelect}
          options={origin.data}
          loading={origin.isLoading}
          onChange={(e) => {
            //TODO: This fix was necessary for allow to set on the map global state the origin data, this is only use on the map container
            const newNotifyChange =
              originSettings.notifyChange !== undefined && originSettings.notifyChange !== null
                ? originSettings.notifyChange
                : false;
            origin.selectOrigin(e);
            if (newNotifyChange) {
              setSelectedOriginFn(e);
              setChangedOriginFn(true);
            }
          }}
          onClear={() => {
            origin.selectOrigin({ value: '' });
            setActiveDone(true);
          }}
          itemRenderer={(item, locationProps) => renderItem(item, locationProps, 'origin')}
          dataTestId="locationSelector-origin"
          placeholder={translate(placeholderOriginKey, 'Select Departure City')}
          isDirty={isDirty}
          onBlur={() => {
            if (isDirty) {
              setWasClear(false);
            }
          }}
          defaultIsOpen={originSettings.isMobile}
          setIsClearOriginFocus={setIsClearOriginFocus}
          setIsClearDestinationFocus={setIsClearDestinationFocus}
          isClearOriginFocus={isClearOriginFocus}
          isClearDestinationFocus={isClearDestinationFocus}
          setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
          setCloseButtonFocus={setCloseButtonFocus}
          closeButtonFocus={closeButtonFocus}
          name="Origin"
        />
      </Box>
    </FlexibleFilterContainer>
  );

  const renderDestinationLocationSelector = (
    <FlexibleFilterContainer
      key="destinationFilter"
      {...containerSettings}
      showSeparator
      saveOnClose={false}
      variant="layout.popover"
      label={titleDestination}
      title={titleDestination}
      disabledClearButton={!Boolean(destination.valueSelect)}
      disabledDoneButton={activeDone ? !activeDone : !Boolean(destination.valueSelect)}
      enabledClearButton
      customAccessibilityToggleElement={true}
      labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
      labelClearBtn={translate(clearButtonLabelKey, 'Clear')}
      onClearBtn={() => {
        destination.onClear();
        setActiveDone(true);
        destinationLocationSelectorRef?.current?.focus();
      }}
      onSave={() => {
        destination.onChange({
          value: destination.valueSelect ? destination.valueSelect : undefined
        });
      }}
      onOpen={() => {
        if (destination.valueSelect !== destination.value) {
          destination.selectDestination({ value: destination.value });
        }
        if (activeDone) {
          setActiveDone(false);
        }
      }}
      onOpenPopup={() => {
        if (destination.valueSelect !== destination.value) {
          destination.selectDestination({ value: destination.value });
        }

        if (activeDone) {
          setActiveDone(false);
        }
        setTimeout(() => destinationLocationSelectorRef?.current?.focus(), 100);
      }}
      toggleElement={
        <FilterButton
          {...destinationSettings.inputItem}
          id={`toggleElementIdDestination-${id}`}
          value={buttonDestinationLabel}
          active={Boolean(destination.value)}
        />
      }
    >
      <Box {...destinationSettings.destinationContainerSetting}>
        <LocationSelector
          flex="1"
          showLabel={showLabels}
          inputFocusReferences={destinationLocationSelectorRef}
          {...destinationSettings}
          defaultValue={destination.valueSelect}
          options={destination.data}
          loading={destination.isLoading || forcedLoading}
          onChange={destination.selectDestination}
          onClear={() => {
            destination.selectDestination({ value: '' });
            setActiveDone(true);
          }}
          itemRenderer={(item, locationProps) => renderItem(item, locationProps, 'destination')}
          dataTestId="locationSelector-destination"
          placeholder={translate(placeholderDestinationKey, 'Select Destination City')}
          isDirty={isDirty}
          onBlur={() => {
            if (isDirty) {
              setWasClear(false);
            }
          }}
          name="Destination"
          labelKey="destination"
          defaultIsOpen={destinationSettings.isMobile}
          setIsClearDestinationFocus={setIsClearDestinationFocus}
          setIsClearOriginFocus={setIsClearOriginFocus}
          isClearOriginFocus={isClearOriginFocus}
          isClearDestinationFocus={isClearDestinationFocus}
          setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
          setCloseButtonFocus={setCloseButtonFocus}
          closeButtonFocus={closeButtonFocus}
        />
      </Box>
    </FlexibleFilterContainer>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <Box {...originDestinationContainerSettings}>
          {showOrigin && (
            <Box
              key="Origin"
              marginRight="8px"
              marginBottom="12px"
              floatLeft
              {...columnOriginSettings}
            >
              {renderOriginLocationSelector}
            </Box>
          )}
          {showDestination && (
            <Box
              key="Destination"
              marginRight="8px"
              marginBottom="12px"
              floatLeft
              {...columnDestinationSettings}
            >
              {renderDestinationLocationSelector}
            </Box>
          )}
        </Box>
      </ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(OriginDestinationFilter);
